import React from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../../store/slices/authentication";
import { SharedHorizontalTabs } from "../../shared";
import BlockedFeatureBanner from "../../shared/BlockedFeatureBanner";
import TreeView from "./TreeView";
import ConnectionPathFinder from "../FamilyConnect/ConnectionPathFinder";
import SurnameBranches from "./SurnameBranches";

const TABS = [
  { key: "treeView", label: "Tree View" },
  { key: "surnameBranches", label: "Surname Branches" },
  { key: "cousinLookup", label: "Cousin Lookup" },
];

// The genealogy office's official record cutoff. Records entered after this date
// may appear, but only data through this date is considered authoritative.
const RECORDS_CURRENT_THROUGH = "December 31, 2022";

function TreeDataDisclaimer() {
  return (
    <div
      role="note"
      className="mx-auto flex max-w-[880px] items-start gap-x-[14px] rounded-[10px] border border-dupontGold/50 bg-dupontGold/10 px-[24px] py-[18px] dark:bg-dupontGold/[0.07]"
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
        className="mt-[2px] shrink-0"
      >
        <circle cx="8" cy="8" r="7" stroke="#b5a03e" strokeWidth="1.5" />
        <line x1="8" y1="7" x2="8" y2="11.5" stroke="#b5a03e" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="8" cy="4.75" r="0.9" fill="#b5a03e" />
      </svg>
      {/* Sized for readability, not decoration — this is the one sentence on the
          page that every member needs to be able to read without leaning in. */}
      <p className="text-left text-[18px] font-body leading-[1.6] text-dupontBlue dark:text-darkText">
        <span className="font-bold">
          Records are current through {RECORDS_CURRENT_THROUGH}.
        </span>{" "}
        Some entries may reflect more recent updates, but {RECORDS_CURRENT_THROUGH} is
        the official baseline for the information shown here.
      </p>
    </div>
  );
}

function FamilyTree() {
  const user = useSelector(selectUser);
  const individualId = user?.externalId ? parseInt(user.externalId) : null;
  const { currentTabIndex, setSearchParams } =
    SharedHorizontalTabs.useGetTabIndex(TABS);

  const handleTabChange = (index) => {
    setSearchParams({ tab: TABS[index].key }, { replace: true });
  };

  return (
    <div className="flex flex-col h-full p-[24px] gap-y-[24px] bg-dupontCream dark:bg-darkPrimary transition-colors duration-200">
      <BlockedFeatureBanner
        featureKey="is_tree_blocked"
        featureName="Family Tree"
        individualId={individualId}
        mode="banner"
      />
      <div className="flex flex-col items-center text-center">
        <h1 className="text-[42px] font-display font-bold text-dupontBlue dark:text-darkText mb-[12px]">
          Family Tree
        </h1>
        <div className="w-[80px] h-[1px] bg-dupontGold"></div>
      </div>
      <TreeDataDisclaimer />
      <SharedHorizontalTabs.Component
        tabs={TABS.map((tab) => tab.label)}
        initialIndex={currentTabIndex}
        onChange={handleTabChange}
      />
      {/* All tabs stay mounted so switching is instant. */}
      <div className={currentTabIndex === 0 ? "flex flex-col grow" : "hidden"}>
        <TreeView />
      </div>
      <div className={currentTabIndex === 1 ? "flex flex-col grow" : "hidden"}>
        <SurnameBranches />
      </div>
      <div className={currentTabIndex === 2 ? "flex flex-col grow" : "hidden"}>
        <ConnectionPathFinder />
      </div>
    </div>
  );
}

export default FamilyTree;
